import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Default section order and expanded state for Company Detail page
 */
const DEFAULT_COMPANY_SECTIONS = [
  { id: 'notes', defaultExpanded: true },
  { id: 'tags', defaultExpanded: true },
  { id: 'invoices', defaultExpanded: true },
  { id: 'payments', defaultExpanded: false },
  { id: 'contacts', defaultExpanded: true },
  { id: 'tickets', defaultExpanded: true },
  { id: 'paymentMethods', defaultExpanded: false },
  { id: 'recurringPayments', defaultExpanded: false },
  { id: 'autopay', defaultExpanded: false },
  { id: 'agreements', defaultExpanded: true },
  { id: 'projects', defaultExpanded: false },
  { id: 'configurations', defaultExpanded: false },
  { id: 'opportunities', defaultExpanded: false },
  { id: 'portalUsers', defaultExpanded: false },
  { id: 'featureFlags', defaultExpanded: false },
  { id: 'paymentSettings', defaultExpanded: false },
  { id: 'displaySettings', defaultExpanded: false },
  { id: 'branding', defaultExpanded: false },
  { id: 'comanaged', defaultExpanded: false },
];

/**
 * Default section order and expanded state for Admin Dashboard page
 */
const DEFAULT_ADMIN_DASHBOARD_SECTIONS = [
  { id: 'statsCards', defaultExpanded: true },
  { id: 'paymentAlerts', defaultExpanded: true },
  { id: 'quoteQuestions', defaultExpanded: true },
  { id: 'recentTickets', defaultExpanded: true },
  { id: 'openInvoices', defaultExpanded: true },
  { id: 'activeAgreements', defaultExpanded: true },
  { id: 'usersByRole', defaultExpanded: true },
  { id: 'recentActivity', defaultExpanded: true },
  { id: 'accountsReceivable', defaultExpanded: true },
  { id: 'quickLinks', defaultExpanded: true },
];

/**
 * Default section visibility for Customer Dashboard page
 */
const DEFAULT_CUSTOMER_DASHBOARD_SECTIONS = [
  { id: 'stats', visible: true },
  { id: 'recentTickets', visible: true },
  { id: 'openInvoices', visible: true },
  { id: 'quickActions', visible: true },
];

/**
 * Merge stored sections with defaults to add any new sections
 */
function mergeSectionsWithDefaults(storedSections, defaultSections) {
  if (!storedSections || !Array.isArray(storedSections)) {
    return defaultSections;
  }

  const storedIds = new Set(storedSections.map(s => s.id));
  const missingSections = defaultSections.filter(s => !storedIds.has(s.id));

  // Add missing sections at the end
  return [...storedSections, ...missingSections];
}

/**
 * User preferences store using Zustand with localStorage persistence
 */
export const usePreferencesStore = create(
  persist(
    (set) => ({
      companySections: DEFAULT_COMPANY_SECTIONS,
      adminDashboardSections: DEFAULT_ADMIN_DASHBOARD_SECTIONS,
      customerDashboardSections: DEFAULT_CUSTOMER_DASHBOARD_SECTIONS,

      /**
       * Set the entire section order for company page (used after drag-and-drop)
       */
      setSectionOrder: (sections) => set({ companySections: sections }),

      /**
       * Set the entire section order for admin dashboard (used after drag-and-drop)
       */
      setAdminDashboardSectionOrder: (sections) => set({ adminDashboardSections: sections }),

      /**
       * Toggle the defaultExpanded state for a company section
       */
      toggleSectionExpanded: (sectionId) => set(state => ({
        companySections: state.companySections.map(s =>
          s.id === sectionId ? { ...s, defaultExpanded: !s.defaultExpanded } : s
        )
      })),

      /**
       * Toggle the defaultExpanded state for an admin dashboard section
       */
      toggleAdminDashboardSectionExpanded: (sectionId) => set(state => ({
        adminDashboardSections: state.adminDashboardSections.map(s =>
          s.id === sectionId ? { ...s, defaultExpanded: !s.defaultExpanded } : s
        )
      })),

      /**
       * Update a single section's expanded state
       */
      setSectionExpanded: (sectionId, expanded) => set(state => ({
        companySections: state.companySections.map(s =>
          s.id === sectionId ? { ...s, defaultExpanded: expanded } : s
        )
      })),

      /**
       * Reset company sections to default order and state
       */
      resetToDefaults: () => set({ companySections: [...DEFAULT_COMPANY_SECTIONS] }),

      /**
       * Reset admin dashboard sections to default order and state
       */
      resetAdminDashboardToDefaults: () => set({ adminDashboardSections: [...DEFAULT_ADMIN_DASHBOARD_SECTIONS] }),

      /**
       * Toggle visibility of a customer dashboard section
       */
      toggleCustomerDashboardSection: (sectionId) => set(state => ({
        customerDashboardSections: state.customerDashboardSections.map(s =>
          s.id === sectionId ? { ...s, visible: !s.visible } : s
        )
      })),

      /**
       * Reset customer dashboard sections to defaults
       */
      resetCustomerDashboardToDefaults: () => set({ customerDashboardSections: [...DEFAULT_CUSTOMER_DASHBOARD_SECTIONS] }),

      /**
       * Get the default company sections (for comparison/reset)
       */
      getDefaults: () => DEFAULT_COMPANY_SECTIONS,

      /**
       * Get the default admin dashboard sections (for comparison/reset)
       */
      getAdminDashboardDefaults: () => DEFAULT_ADMIN_DASHBOARD_SECTIONS,

      /**
       * Get the default customer dashboard sections
       */
      getCustomerDashboardDefaults: () => DEFAULT_CUSTOMER_DASHBOARD_SECTIONS,
    }),
    {
      name: 'halomts-preferences',
      merge: (persistedState, currentState) => {
        // Merge persisted state with current state, adding any missing sections
        const mergedCompanySections = mergeSectionsWithDefaults(
          persistedState?.companySections,
          DEFAULT_COMPANY_SECTIONS
        );
        const mergedAdminSections = mergeSectionsWithDefaults(
          persistedState?.adminDashboardSections,
          DEFAULT_ADMIN_DASHBOARD_SECTIONS
        );
        const mergedCustomerDashSections = mergeSectionsWithDefaults(
          persistedState?.customerDashboardSections,
          DEFAULT_CUSTOMER_DASHBOARD_SECTIONS
        );

        return {
          ...currentState,
          ...persistedState,
          companySections: mergedCompanySections,
          adminDashboardSections: mergedAdminSections,
          customerDashboardSections: mergedCustomerDashSections,
        };
      },
    }
  )
);
